import { useState, useEffect, memo } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  FormControl,
  Select,
  ListItemIcon,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Palette,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useSettings, type Locale } from '@/contexts/SettingsContext';
import SettingsDrawer from '@/components/common/SettingsDrawer';
import NotificationPanel from '@/components/NotificationPanel';
import { notificationService } from '@/services/notification.service';
import { useTranslation } from 'react-i18next';

const LOCALE_FLAGS: Record<Locale, string> = {
  en: '🇺🇸',
  zh: '🇨🇳',
  de: '🇩🇪',
};

interface AppBarProps {
  onMenuClick: () => void;
}

/**
 * 顶部导航栏组件
 * 使用 memo 优化性能，避免不必要的重渲染
 */
function AppBar({ onMenuClick }: AppBarProps) {
  const navigate = useNavigate();
  const { userInfo, logout } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { t } = useTranslation();
  const { locale, setLocale } = useSettings();

  // 获取未读通知数量（兼容 data 被包装一层的情况）
  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount() as { count?: number; data?: { count?: number } };
      const count = response?.count ?? response?.data?.count;
      if (typeof count === 'number' && count >= 0) {
        setUnreadCount(count);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  // 定期轮询未读数量
  useEffect(() => {
    if (userInfo) {
      fetchUnreadCount();

      // 每30秒轮询一次
      const intervalId = setInterval(fetchUnreadCount, 30000);

      return () => clearInterval(intervalId);
    }
  }, [userInfo]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleSettingsOpen = () => {
    handleMenuClose();
    setSettingsOpen(true);
  };

  return (
    <MuiAppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          Lotus22 Admin
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 88 }}>
            <Select
              value={locale}
              onChange={(e) => setLocale(e.target.value as Locale)}
              variant="outlined"
              size="small"
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <span style={{ fontSize: '1.1rem' }}>{LOCALE_FLAGS[value as Locale]}</span>
                  <span>{value === 'en' ? t('settings.english') : value === 'zh' ? t('settings.chinese') : t('settings.german')}</span>
                </Box>
              )}
              sx={{
                color: 'inherit',
                height: 32,
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' },
                '& .MuiSvgIcon-root': { color: 'inherit' },
              }}
            >
              <MenuItem value="en">
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <span style={{ fontSize: '1.25rem' }}>{LOCALE_FLAGS.en}</span>
                </ListItemIcon>
                {t('settings.english')}
              </MenuItem>
              <MenuItem value="zh">
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <span style={{ fontSize: '1.25rem' }}>{LOCALE_FLAGS.zh}</span>
                </ListItemIcon>
                {t('settings.chinese')}
              </MenuItem>
              <MenuItem value="de">
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <span style={{ fontSize: '1.25rem' }}>{LOCALE_FLAGS.de}</span>
                </ListItemIcon>
                {t('settings.german')}
              </MenuItem>
            </Select>
          </FormControl>
          <IconButton
            color="inherit"
            onClick={() => setNotificationOpen(true)}
            size="small"
            title={t('notifications.title')}
          >
            <Badge badgeContent={unreadCount} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {userInfo?.nickname || userInfo?.name || userInfo?.username}
            </Typography>
            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.25 }}>
              <Avatar
                alt={userInfo?.nickname || userInfo?.name || userInfo?.username}
                src={userInfo?.avatar}
                sx={{ width: 32, height: 32 }}
              >
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
        >
          <MenuItem onClick={handleProfile}>
            <AccountCircle sx={{ mr: 1 }} />
            {t('common.settings')}
          </MenuItem>
          <MenuItem onClick={handleSettingsOpen}>
            <Palette sx={{ mr: 1 }} />
            {t('settings.theme')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            {t('common.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
      <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <NotificationPanel
        open={notificationOpen}
        onClose={() => setNotificationOpen(false)}
        onUnreadCountChange={setUnreadCount}
      />
    </MuiAppBar>
  );
}

// 使用 memo 优化，避免父组件更新时不必要的重渲染
export default memo(AppBar);
